import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Layers, Loader2, Plus, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { CardContent, CardFooter, CardIcon } from '@/components/ui/card'
import { apiErrorDescription } from '@/services/core/apiError'
import type { Network } from '@/services/networks/networksApi'
import { TiersApi, type TierDetail } from '@/services/networks/tiersApi'
import { useNetwork } from '../../contexts/NetworkContext'
import { AppShell } from '../../components/AppShell'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { TierPriorityTable } from '../../components/TierPriorityTable'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'

/** The network's enabled tiers in priority order, leaving out any that no longer load. */
function networkTierIds(network: Network | null, tiers: TierDetail[]): string[] {
  return (network?.tiers ?? [])
    .map((tier) => tier.id)
    .filter((id) => tiers.some((tier) => tier.id === id))
}

export function TiersPage() {
  const { currentNetwork } = useNetwork()

  const { data: tiers, isLoading } = useQuery({
    queryKey: ['tiers'],
    queryFn: () => TiersApi.getAll(),
    staleTime: Infinity,
  })

  if (isLoading || !tiers || !currentNetwork) {
    return <TiersList tiers={[]} network={currentNetwork} loading />
  }
  // Remount when the saved order changes, so the form starts from it again.
  const key = `${currentNetwork.tagId}:${currentNetwork.tiers.map((tier) => tier.id).join()}`
  return <TiersList key={key} tiers={tiers} network={currentNetwork} />
}

interface TiersListProps {
  tiers: TierDetail[]
  network: Network | null
  loading?: boolean
}

function TiersList({ tiers, network, loading }: TiersListProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const form = useForm({
    defaultValues: { enabledIds: networkTierIds(network, tiers) },
    onSubmit: ({ value }) => mutation.mutate(value.enabledIds),
  })
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const mutation = useMutation({
    mutationFn: (enabledIds: string[]) => TiersApi.setNetworkTiers(network!, enabledIds),
    onSuccess: (data) => {
      markClean()
      queryClient.setQueryData(['network', data.tagId], data)
      queryClient.invalidateQueries({ queryKey: ['tier'] })
      toast.success(t('common.saved'), { description: t('tiers.prioritySaved'), duration: 3000 })
    },
    onError: (error) => {
      toast.error(t('tiers.prioritySaveFailed'), {
        description: apiErrorDescription(error),
        duration: 5000,
      })
    },
  })

  return (
    <AppShell crumbs={[{ label: t('tiers.title') }]}>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('tiers.title')}</h1>
            <p>{t('tiers.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <Button
              type="button"
              onClick={() => navigate(`/admin/tiers/${crypto.randomUUID().replace(/-/g, '').slice(0, 16)}?new=true`)}
            >
              <Plus className="size-4" /> {t('tiers.newTier')}
            </Button>
          </div>
        </div>

        <ListCard>
          <ListCardHeader
            title={
              <>
                <CardIcon icon={Layers} />
                {t('tiers.title')}
              </>
            }
            count={tiers.length}
          />

          <CardContent className="p-0">
            <form.Field name="enabledIds">
              {(field) => (
                <TierPriorityTable
                  tiers={tiers}
                  enabledIds={field.state.value}
                  onChange={(ids) => field.handleChange(ids)}
                  loading={loading}
                />
              )}
            </form.Field>
          </CardContent>

          {tiers.length > 0 && (
            <CardFooter>
              <span className="text-xs text-(--ad-text-muted)">{t('tiers.priorityHint')}</span>
            </CardFooter>
          )}
        </ListCard>

        <div className={`detail-savebar${isDirty || mutation.isPending ? ' is-visible' : ''}`}>
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                form.reset()
                markClean()
              }}
              disabled={mutation.isPending}
            >
              <X className="size-4" />
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              <Check className="size-4" />
              {t('tiers.savePriority')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}
